// The checks this run owes, one at a time.
//
// THE THREE RULES THIS SCREEN IS BUILT AROUND.
//
// 1. THE PARAMETERS COME FROM THE RUN, NOT FROM /specs. Run.SpecVersionId is fixed
//    at run start and every reading is judged against it, so this screen reads the
//    run's own payload. Fetching the live spec instead would put version 4's
//    limits on a screen capturing into a run pinned to version 3 — the exact
//    re-judging that column exists to prevent.
//
// 2. THE ORDER IS THE SPEC'S, NOT OURS. SpecParameter.Sequence is a spec property
//    precisely because the order is sometimes physical — weigh before you open,
//    swab before you clean. Sorting by id, or by what is due soonest, would
//    silently reorder the operator's screen when somebody edits a spec.
//
// 3. COMMIT ENQUEUES; IT DOES NOT POST. The queue stamps the capture id, the UPN
//    and the device time inside its own write, and it survives the dead spot at
//    the far end of the line. A direct fetch from here would lose the reading the
//    moment the wifi drops, which is the failure the queue exists for.
//
// And the verdict on this screen is a PREVIEW. It knows the numbers and the bands
// and nothing else — not calibration, not competency, not authority, none of which
// a device can check while offline. The server's gate decides, it stores what it
// decided, and where the two ever disagree the server is right.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { usePageTitle } from '../../lib/pageTitle';
import { AGGREGATION_LABEL } from '../../lib/vocabulary';
import { VerdictBadge } from '../../components/VerdictBadge';
import { PilotBadge } from '../../components/Pilot';
import { useQueue } from '../../offline/useQueue';
import { opFocus, opMessage } from '../../shells/OperatorShell';
import { CloseRun, isDueBy } from './CloseRun';
import { Keypad, parsesAsNumber } from './Keypad';
import { Walkaround } from './Walkaround';
import { LinePhotoPicker, PhotoRefusedBanner, ReferenceThumbs } from './LinePhotos';
import {
  evictReferencePhotos,
  listedReferences,
  offerPhotoForVerdict,
  type ReferencePhoto,
} from './linePhotoModel';
import type { DraftPhoto } from './receiveModel';

/* ── What this screen needs from the API ──────────────────
 * Three reads, because no single route carries all of it. Each interface below
 * is the shape the route ACTUALLY answers with — they are envelopes, not bare
 * rows, and a component that treats one as an array gets an empty screen with no
 * error on it.
 */

type Verdict = 'IN' | 'ACTION' | 'OUT';

/** The flat parameter this screen works in. The API nests the two bands; they
 *  are flattened once, at the edge, so the verdict preview and the ruler are not
 *  each reaching into `rejectLimits.min`. */
interface SpecParameter {
  id: number;
  code: string;
  name: string;
  unit: string | null;
  sequence: number;
  minValue: number | null;
  maxValue: number | null;
  actionLower: number | null;
  actionUpper: number | null;
  nominal: number | null;
  sampleCount: number;
  aggregation: 'ALL' | 'MEAN' | 'AQS';
  controlPointType: 'NONE' | 'CCP' | 'OPRP';
  requiresEvidence: boolean;
  requiresInstrument: boolean;
  instrumentKind: string | null;
  /** Migration 034. Absent = not known (rule 15), [] = none. */
  referencePhotos?: ReferencePhoto[] | null;
}

/** GET /specs?subjectType=PRODUCT&subjectId=&at= — the version in force for a
 *  subject AT an instant. Asked with the run's start time, so it resolves the
 *  version the run is pinned to rather than whatever is live now. */
interface SpecPayload {
  inForce: boolean;
  message?: string | null;
  version: { id: number; version: number; status: string } | null;
  parameters: Array<{
    id: number;
    code: string;
    name: string;
    unit: string | null;
    sequence: number;
    controlPointType: 'NONE' | 'CCP' | 'OPRP';
    sampleCount: number;
    aggregation: 'ALL' | 'MEAN' | 'AQS';
    rejectLimits: { min: number | null; max: number | null };
    actionLimits: { lower: number | null; upper: number | null };
    nominal: number | null;
    requiresEvidence: boolean;
    requiresInstrument: boolean;
    instrumentKind: string | null;
    referencePhotos?: ReferencePhoto[] | null;
  }>;
}

function flatten(p: SpecPayload['parameters'][number]): SpecParameter {
  return {
    id: p.id,
    code: p.code,
    name: p.name,
    unit: p.unit,
    sequence: p.sequence,
    minValue: p.rejectLimits?.min ?? null,
    maxValue: p.rejectLimits?.max ?? null,
    actionLower: p.actionLimits?.lower ?? null,
    actionUpper: p.actionLimits?.upper ?? null,
    nominal: p.nominal ?? null,
    sampleCount: p.sampleCount,
    aggregation: p.aggregation,
    controlPointType: p.controlPointType,
    requiresEvidence: Boolean(p.requiresEvidence),
    requiresInstrument: Boolean(p.requiresInstrument),
    instrumentKind: p.instrumentKind,
    referencePhotos: p.referencePhotos,
  };
}

/** GET /runs/{id} — quality_run_status. The run, the version it is pinned to,
 *  and its recent readings; NOT a flat row and NOT the spec parameters. */
interface RunStatus {
  run: {
    id: number;
    status: 'OPEN' | 'CLOSED';
    startedAt: string;
    d365BatchNumber: string | null;
    line: { id: number; code: string | null; name: string | null };
    product: { id: number; code: string | null; name: string | null };
    /** Started in pilot mode (rule 27). Absent on an older payload = controlled. */
    isPilot?: boolean;
  };
  /** Pilot mode as the server sees it for THIS run (rule 27). */
  pilot?: { isPilot: boolean; acceptsNewEntries: boolean };
  specVersion: { id: number; version: number } | null;
  /** Added in slice 1b. Absent on an older payload, which is why every read of
   *  it is guarded — an undefined here must not read as "released". */
  readiness?: {
    total: number;
    outstanding: number;
    released: boolean;
    hasForm: boolean;
  };
}

/** GET /runs/{id}/obligations — quality_owed_checks. The obligation's own id is
 *  `obligationId`, not `id`: the row joins several tables that each have one. */
interface Obligation {
  obligationId: number;
  /** READINESS rows come back too while the walk-around is open; the keypad never lists them
   *  (their parameter is on the LINE spec, not this product's). */
  kind: 'FIRST_OFF' | 'ADHERENCE' | 'READINESS';
  dueAt: string;
  status: 'OWED' | 'AWAITING_RESULT' | 'MET' | 'MISSED';
  overdue: boolean;
  parameter: { id: number; code: string; name: string };
}

/**
 * When a check is wanted, in the fewest words that still say it.
 *
 * "due now" rather than a time for anything already due, because a clock reading
 * on a card the operator is meant to act on immediately is noise. A future check
 * gets its actual time, which is the whole point: it is the only thing telling
 * two same-named cards apart.
 */
function dueLabel(o: { dueAt: string; overdue: boolean }, nowMs: number): string {
  const at = new Date(o.dueAt);
  if (Number.isNaN(at.getTime())) return '';
  if (o.overdue) return 'overdue';
  if (at.getTime() <= nowMs) return 'due now';
  return `due ${at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
}

// isDueBy lives in CloseRun.tsx now (UI/UX audit, 31 Oct 2026, UX-40): the panel counts what
// closing books missed with the same test this screen uses for "due now".

/**
 * The time, re-read every `everyMs`.
 *
 * In-process checks recur, so a check shown under "Later" at 9:06 is due at
 * 10:06 and has to move up by itself. Nothing else on this screen changes on
 * its own at that moment, so without a tick it would sit under "Later" until
 * somebody touched something.
 */
function useNow(everyMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), everyMs);
    return () => window.clearInterval(t);
  }, [everyMs]);
  return now;
}

/** GET /meters — quality_meter_status. `blocksCapture` and `blockReason` are the
 *  server's own answer; this screen does not re-derive whether an instrument is
 *  overdue, because a second copy of that arithmetic is a second chance to get
 *  the sign wrong. */
interface Meter {
  id: number;
  code: string;
  name: string;
  kind: string;
  status: 'IN_SERVICE' | 'OUT_OF_SERVICE';
  calibrationRequired: boolean;
  calibrationDueAt: string | null;
  blocksCapture: boolean;
  blockReason: string | null;
}

/* ── The verdict preview ──────────────────────────────────
 * A deliberate second copy of api/src/domain/verdict.ts, and the only duplicated
 * rule in the SPA. The device is sometimes offline and cannot ask, and a number
 * typed with no feedback at all is worse than one with a preview that the server
 * may overrule. Keep the two in step; the server's is the one that is stored.
 */

function verdictForValue(v: number, p: SpecParameter): Verdict {
  if (p.minValue !== null && v < p.minValue) return 'OUT';
  if (p.maxValue !== null && v > p.maxValue) return 'OUT';
  if (p.actionLower !== null && v < p.actionLower) return 'ACTION';
  if (p.actionUpper !== null && v > p.actionUpper) return 'ACTION';
  return 'IN';
}

const RANK: Record<Verdict, number> = { IN: 0, ACTION: 1, OUT: 2 };

function previewVerdict(values: number[], p: SpecParameter): Verdict | null {
  if (values.length === 0) return null;
  if (p.aggregation === 'MEAN') {
    return verdictForValue(values.reduce((s, v) => s + v, 0) / values.length, p);
  }
  if (p.aggregation === 'AQS') {
    // AQS is a named rule, not a generic average: the mean must be at or above
    // nominal, at most one pack may be below T1, and none below T2. Without those
    // three numbers on the spec there is nothing to preview — and the server will
    // reject the capture for the same reason.
    const t1 = p.actionLower;
    const t2 = p.minValue;
    if (p.nominal === null || t1 === null || t2 === null) return null;
    const m = values.reduce((s, v) => s + v, 0) / values.length;
    if (values.some((v) => v < t2)) return 'OUT';
    if (m < p.nominal) return 'OUT';
    const belowT1 = values.filter((v) => v < t1).length;
    if (belowT1 > 1) return 'OUT';
    return belowT1 === 1 ? 'ACTION' : 'IN';
  }
  // ALL: any one sample can condemn the check.
  let worst: Verdict = 'IN';
  for (const v of values) {
    const s = verdictForValue(v, p);
    if (RANK[s] > RANK[worst]) worst = s;
  }
  return worst;
}

/* ── Band ruler ───────────────────────────────────────────
 * Drawn as SVG rather than positioned divs: the geometry here is DATA (where this
 * number sits between the action limit and the reject limit), and it belongs in
 * x/width attributes rather than in a style attribute the design system cannot
 * see. Colours are token references, so the ruler themes with everything else.
 */

function bandRulerScale(p: SpecParameter, values: number[]) {
  const pts = [p.minValue, p.maxValue, p.actionLower, p.actionUpper, p.nominal, ...values].filter(
    (n): n is number => typeof n === 'number' && Number.isFinite(n),
  );
  if (pts.length === 0) return null;
  let lo = Math.min(...pts);
  let hi = Math.max(...pts);
  if (hi === lo) {
    lo -= 1;
    hi += 1;
  }
  // Pad so a value past the reject limit is drawn OUTSIDE the band and visibly so,
  // rather than pinned to the edge where it reads as "just on the line".
  const pad = (hi - lo) * 0.12;
  return { lo: lo - pad, hi: hi + pad };
}

const VERDICT_FILL: Record<Verdict, string> = {
  IN: 'var(--ok-line)',
  ACTION: 'var(--warn-line)',
  OUT: 'var(--bad-line)',
};

function BandRuler({ p, values }: { p: SpecParameter; values: number[] }) {
  const scale = bandRulerScale(p, values);
  if (!scale) return null;
  const { lo, hi } = scale;
  const at = (v: number) => ((v - lo) / (hi - lo)) * 100;

  const rejectLo = p.minValue ?? lo;
  const rejectHi = p.maxValue ?? hi;
  // With no action limits the action band IS the reject band, so everything in
  // spec draws as IN — which is what a spec with one band means.
  const actionLo = p.actionLower ?? rejectLo;
  const actionHi = p.actionUpper ?? rejectHi;

  return (
    <div>
      <div className="overflow-hidden rounded-(--radius-sm)">
        <svg
          viewBox="0 0 100 12"
          preserveAspectRatio="none"
          role="img"
          aria-label={`Band ruler: reject ${p.minValue ?? '—'} to ${p.maxValue ?? '—'}, action ${
            p.actionLower ?? '—'
          } to ${p.actionUpper ?? '—'}`}
          className="block h-[22px] w-full"
        >
          <rect x="0" y="0" width="100" height="12" fill="var(--bad-bg)" />
          <rect
            x={at(rejectLo)}
            y="0"
            width={Math.max(0, at(rejectHi) - at(rejectLo))}
            height="12"
            fill="var(--warn-bg)"
          />
          <rect
            x={at(actionLo)}
            y="0"
            width={Math.max(0, at(actionHi) - at(actionLo))}
            height="12"
            fill="var(--ok-bg)"
          />
          {p.nominal !== null ? (
            <rect x={at(p.nominal) - 0.2} y="0" width="0.4" height="12" fill="var(--ok-line)" />
          ) : null}
          {values.map((v, i) => (
            <rect
              key={i}
              x={Math.min(99.5, Math.max(0, at(v) - 0.45))}
              y="0"
              width="0.9"
              height="12"
              fill={VERDICT_FILL[verdictForValue(v, p)]}
            />
          ))}
        </svg>
      </div>
      <div className="mt-(--s1) flex justify-between font-mono text-(length:--fs-xs) text-(--muted-2)">
        <span>{p.minValue ?? 'no lower reject'}</span>
        <span>{p.nominal !== null ? `target ${p.nominal}` : ''}</span>
        <span>{p.maxValue ?? 'no upper reject'}</span>
      </div>
    </div>
  );
}

/* ── The page ─────────────────────────────────────────── */

interface OwedCheck {
  obligation: Obligation;
  param: SpecParameter;
}

function bandText(lo: number | null, hi: number | null, unit: string | null): string {
  if (lo === null && hi === null) return 'not set';
  const u = unit ? ` ${unit}` : '';
  if (lo === null) return `up to ${hi}${u}`;
  if (hi === null) return `from ${lo}${u}`;
  return `${lo} – ${hi}${u}`;
}

/** Why this instrument cannot be used, in the server's own words. `blocksCapture`
 *  is what the ingest gate will apply; echoing it here saves a refusal rather
 *  than inventing a second rule that could disagree with the one that decides. */
function meterBlock(m: Meter): string | null {
  if (!m.blocksCapture) return null;
  return `${m.code}: ${m.blockReason ?? 'this instrument cannot be used for a check.'}`;
}

export function LineCheck() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const { enqueue, queued, photosPending } = useQueue();
  /** Readings still on the device, photos aside: the re-read below waits on READINGS.
   *  A photo waiting out a Blob outage must not hold the next check off the screen. */
  const readingsQueued = queued === null || photosPending === null ? null : queued - photosPending;
  const nowMs = useNow(30_000);

  const [run, setRun] = useState<RunStatus | null>(null);
  // The tab names the run and its line (UI/UX audit, 31 Oct 2026, UX-21): "Run 2 · Bhuja Fryer · Line check · Quality".
  usePageTitle(runId ? `Run ${run?.run.id ?? runId}${run?.run.line.name ? ` · ${run.run.line.name}` : ''}` : null);
  const [parameters, setParameters] = useState<SpecParameter[]>([]);
  const [obligations, setObligations] = useState<Obligation[]>([]);
  const [meters, setMeters] = useState<Meter[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [focusedId, setFocusedId] = useState<number | null>(null);
  const [sent, setSent] = useState<number[]>([]);
  const [samples, setSamples] = useState<string[]>(['']);
  const [activeSample, setActiveSample] = useState(0);
  const [meterId, setMeterId] = useState<number | null>(null);
  const [evidence, setEvidence] = useState('');
  /** Photos chosen for THIS check before Commit, on an OUT or ACTION preview only.
   *  They go into the same enqueue as the reading: `advance` moves focus and the
   *  effect below clears them, so there is no "after" on this screen. */
  const [photos, setPhotos] = useState<DraftPhoto[]>([]);
  // Bumped when the walk-around enqueues an answer, so the run is re-read and
  // this screen can switch to the keypad the moment the line is released.
  const [readinessTick, setReadinessTick] = useState(0);

  // Three reads, and the ORDER matters: the run comes first because the spec is
  // then asked for AT the run's start instant. `GET /runs/{id}` carries the
  // pinned version's id but not its parameters, and `GET /specs` is the only
  // route that returns limits — asking it with `at` is what makes the two the
  // same version. Asking it without `at` would hand this screen whatever is live
  // now, which is precisely the re-judging Run.SpecVersionId exists to prevent.
  const load = useCallback(() => {
    if (!runId) return;
    setError(null);
    let cancelled = false;

    void (async () => {
      try {
        const status = (await api.get(`/runs/${runId}`)) as RunStatus;
        if (cancelled) return;
        setRun(status);

        const [obl, met, spec] = await Promise.all([
          api.get(`/runs/${runId}/obligations`) as Promise<{ obligations?: Obligation[] }>,
          api.get('/meters') as Promise<{ meters?: Meter[] }>,
          api.get(
            `/specs?subjectType=PRODUCT&subjectId=${status.run.product.id}` +
              `&at=${encodeURIComponent(status.run.startedAt)}`,
          ) as Promise<SpecPayload>,
        ]);
        if (cancelled) return;

        setObligations(Array.isArray(obl.obligations) ? obl.obligations : []);
        setMeters(Array.isArray(met.meters) ? met.meters : []);

        // The version that came back must BE the one the run is pinned to. If it
        // is not, the bands on this screen are not the bands this product is
        // judged against, and showing them anyway would invite a capture against
        // the wrong limits. Refuse and say so.
        const pinned = status.specVersion?.id ?? null;
        if (!spec.inForce || spec.version === null) {
          setParameters([]);
          setError(
            spec.message ??
              'No approved specification is in force for this product at this run. Tell the office.',
          );
          return;
        }
        if (pinned !== null && spec.version.id !== pinned) {
          setParameters([]);
          setError(
            `This run is pinned to specification version id ${pinned}, but the version in force ` +
              `at its start now resolves to ${spec.version.id}. Capture is stopped until the office ` +
              'sorts out which one this product was made to.',
          );
          return;
        }
        setParameters((spec.parameters ?? []).map(flatten));
        const listed = listedReferences((spec.parameters ?? []).map((p) => p.referencePhotos));
        if (listed) void evictReferencePhotos('spec', listed);
      } catch (e: unknown) {
        if (!cancelled) setError(opMessage(e));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [runId]);

  useEffect(load, [load, readinessTick]);

  // Owed checks in SPEC order. An obligation whose parameter is not in the run's
  // pinned version is surfaced rather than filtered away — a check that quietly
  // disappears from this list is a check nobody does.
  const { owed, orphanCount } = useMemo(() => {
    const byId = new Map(parameters.map((p) => [p.id, p]));
    const matched: OwedCheck[] = [];
    let orphans = 0;
    for (const o of obligations) {
      if (o.status !== 'OWED') continue;
      const param = byId.get(o.parameter.id);
      if (!param) {
        orphans += 1;
        continue;
      }
      matched.push({ obligation: o, param });
    }
    matched.sort(
      (a, b) =>
        a.param.sequence - b.param.sequence || a.obligation.obligationId - b.obligation.obligationId,
    );
    return { owed: matched, orphanCount: orphans };
  }, [obligations, parameters]);

  // ⚠ OWED IS NOT "WANTED NOW" ANY MORE. In-process checks recur — each one met
  // schedules the next an interval later (ingestGate.ts) — so after a round of
  // checks every parameter is owed again, at 10:06. Listing those as owed, and
  // focusing the first one with a live keypad, tells an operator who has just
  // finished to start again. They are split out, and only a check that is DUE is
  // ever focused automatically. Tapping one under "Later" takes it early, which
  // is allowed: it meets that check and the next is scheduled from then.
  const dueNowChecks = useMemo(() => owed.filter((c) => isDueBy(c.obligation, nowMs)), [owed, nowMs]);
  const laterChecks = useMemo(() => owed.filter((c) => !isDueBy(c.obligation, nowMs)), [owed, nowMs]);

  const focused = useMemo(
    () =>
      owed.find((c) => c.obligation.obligationId === focusedId) ??
      dueNowChecks.find((c) => !sent.includes(c.obligation.obligationId)) ??
      null,
    [owed, dueNowChecks, focusedId, sent],
  );

  // ⚠ RE-READ ONCE EVERYTHING KEYED HAS REACHED THE SERVER, and only then. The
  // next in-process check is created server-side when a reading is ACCEPTED, so
  // this screen cannot know it exists until the queue has delivered. `sent` is
  // cleared in the SAME update as the new list: clearing it first would put the
  // just-keyed check back on screen as unkeyed, for as long as the request took,
  // with a live keypad under it.
  //
  // Safe to key on `queued === 0`: the queue publishes its new depth before
  // `enqueue` resolves, so a check is in `sent` only while the depth already
  // counts it — zero with something in `sent` means it has been delivered (or
  // refused, in which case it is still owed and comes back, correctly, as
  // unkeyed; the refusal banner says why). Unknown (null) never triggers this.
  // The clock tick retries a read that failed.
  const rereading = useRef(false);
  useEffect(() => {
    if (!runId || readingsQueued !== 0 || sent.length === 0 || rereading.current) return;
    rereading.current = true;
    (api.get(`/runs/${runId}/obligations`) as Promise<{ obligations?: Obligation[] }>)
      .then((obl) => {
        setObligations(Array.isArray(obl.obligations) ? obl.obligations : []);
        setSent([]);
      })
      .catch(() => undefined)
      .finally(() => {
        rereading.current = false;
      });
  }, [runId, readingsQueued, sent.length, nowMs]);
  const param = focused?.param ?? null;

  // A new check is a clean sheet: samples, instrument and evidence all belong to
  // the check being keyed, never to the one before it.
  //
  // ⚠⚠ KEYED ON THE OBLIGATION, NOT THE PARAMETER, AND THAT IS THE WHOLE POINT.
  // It used to depend on `param?.id`, which is a different thing wearing a
  // similar name. Every one of the 65 product parameters loaded from v6.6 is
  // `CheckKind = BOTH`, so a run creates TWO obligations against the SAME
  // parameter — one FIRST_OFF, one ADHERENCE (runs.ts). Advancing from one to
  // the other changed neither `param.id` nor `param.sampleCount`, so this effect
  // never re-ran: the previous keystrokes stayed in `samples`, the 44px readout
  // still showed the last number, the verdict badge still showed its verdict, and
  // Commit was already live. One tap recorded a measurement nobody took,
  // attributed to the person holding the iPad.
  //
  // That is the single thing this app exists to make impossible, and the comment
  // above it described the correct behaviour the whole time.
  const focusedObligationId = focused?.obligation.obligationId ?? null;
  useEffect(() => {
    if (!param) return;
    setSamples(Array.from({ length: Math.max(1, param.sampleCount) }, () => ''));
    setActiveSample(0);
    setMeterId(null);
    setEvidence('');
    setPhotos([]);
  }, [focusedObligationId, param?.sampleCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const entered = samples.filter(parsesAsNumber).map(Number);
  const allEntered = param ? samples.every(parsesAsNumber) : false;
  const preview = param ? previewVerdict(entered, param) : null;
  const offerPhoto = offerPhotoForVerdict(preview);

  const candidateMeters = useMemo(
    () => (param?.instrumentKind ? meters.filter((m) => m.kind === param.instrumentKind) : meters),
    [meters, param],
  );
  const chosenMeter = candidateMeters.find((m) => m.id === meterId) ?? null;
  const meterProblem = chosenMeter ? meterBlock(chosenMeter) : null;

  // Why commit is unavailable. These mirror the server's gate so the operator is
  // not sent on a round trip to be told something this screen already knows —
  // they are a courtesy, never the gate. The server re-checks all of it.
  const blockedReason: string | null = !param
    ? 'No check is selected.'
    : !allEntered
      ? param.sampleCount > 1
        ? `Enter all ${param.sampleCount} samples.`
        : 'Key a number to commit this check.'
      : param.requiresInstrument && !chosenMeter
        ? 'Choose the instrument this was taken with.'
        : meterProblem
          ? meterProblem
          : param.requiresEvidence && !evidence.trim()
            ? 'This check requires evidence.'
            : null;

  const advance = useCallback(
    (justSent: number) => {
      const done = [...sent, justSent];
      // The next check that is DUE. Advancing into "Later" would put the next
      // round's first check under the operator's thumb the moment this round ends.
      const next = dueNowChecks.find((c) => !done.includes(c.obligation.obligationId));
      setSent(done);
      setFocusedId(next ? next.obligation.obligationId : null);
    },
    [dueNowChecks, sent],
  );

  const commit = useCallback(async () => {
    if (!focused || !runId || blockedReason) return;
    try {
      // Only the measurement. The capture id, the UPN and the device clock are
      // stamped INSIDE the queue's write — stamping them here would let a sign-out
      // between keying and sending re-attribute the reading.
      await enqueue({
        runId: Number(runId),
        specParameterId: focused.param.id,
        meterId: chosenMeter?.id ?? null,
        values: samples.map(Number),
        evidenceRef: evidence.trim() || null,
        // Only while the preview is still bad: a value re-keyed to IN sends none.
        photos: offerPhoto ? photos.map((p) => ({ blob: p.blob, capturedAtDevice: p.capturedAtDevice })) : [],
        // Carried with the capture, not looked up later: a held reading has to be
        // nameable on a device that is offline and cannot ask what run 412 was.
        context: {
          lineCode: run?.run.line.code ?? undefined,
          productCode: run?.run.product.code ?? undefined,
          parameterCode: focused.param.code,
          parameterName: focused.param.name,
          runLabel: `run ${runId}`,
        },
      });
      advance(focused.obligation.obligationId);
    } catch (e: unknown) {
      setError(opMessage(e));
    }
  }, [focused, runId, blockedReason, enqueue, chosenMeter, samples, evidence, advance, run, offerPhoto, photos]);

  // ── THE LINE MUST BE RELEASED BEFORE PRODUCT IS MEASURED ON IT.
  //
  // While any start-up check is outstanding this screen IS the walk-around, and
  // the keypad below is not rendered at all. That is deliberate: a disabled
  // keypad invites somebody to look for the way around it, whereas a screen that
  // is simply the next job does not.
  //
  // ⚠ THIS IS NOT THE ENFORCEMENT. The server's gate refuses any first-off or
  // in-process reading while readiness is outstanding, whatever this screen
  // renders — a queued reading sent from a device that never saw this branch is
  // refused on arrival and stored REJECTED. This is the operator's experience of
  // a rule that lives in gateRules.ts.
  //
  // `readiness` is optional on the payload, so an older API answer leaves it
  // undefined. Undefined means "we do not know", and an unknown must not read as
  // released — hence the explicit `> 0` rather than a truthiness check.
  // What closing would record is read by the Close run panel itself, as it opens (UI/UX audit,
  // 31 Oct 2026, UX-40): from here it was one number read at load, and it disagreed with the
  // walk-around behind it.
  //
  // Back to the line picker: the only way off a run was Close run (found on the phone, 27 Oct 2026).
  // ⚠ TO THIS RUN'S LINE (UI/UX audit, 31 Oct 2026, UX-52): `/op?line=CODE` reopens the picker on
  // the line's open runs, which is where an operator leaving a run wants to be; the picker keeps
  // the line in the URL so reload and Back keep it too. 56px, not 44 (UX-22): the shell's gloved
  // target rule.
  const linesHref = run?.run.line.code ? `/op?line=${encodeURIComponent(run.run.line.code)}` : '/op';
  const linesLink = (
    <Link
      to={linesHref}
      className={`flex min-h-[56px] items-center rounded-(--radius) border border-(--border-strong) bg-(--card) px-(--s3) text-(length:--fs-sm) font-semibold ${opFocus}`}
    >
      ‹ Lines
    </Link>
  );
  const closeControl =
    runId && run?.run.status === 'OPEN' ? (
      <CloseRun
        runId={Number(runId)}
        productName={run.run.product.name ?? run.run.product.code}
        queued={queued}
        onClosed={() => navigate(linesHref)}
      />
    ) : null;

  // No pilot strip on the run screens (Amit, 28 Oct 2026: "remove the pilot warning"). The PILOT
  // badge on the line picker and the office still marks a pilot record for ever (rule 27). The one
  // sentence kept is the one that changes what the operator can do: the window has closed and this
  // run takes no new readings.
  const pilotStrip =
    run?.run.isPilot && run.pilot?.acceptsNewEntries === false ? (
      <div
        role="status"
        className="flex flex-none items-center gap-(--s3) border-b border-(--warn-line) bg-(--warn-bg) px-(--s4) py-(--s1) text-(length:--fs-sm) font-semibold text-(--warn) sm:px-(--s5) sm:py-(--s2)"
      >
        <PilotBadge isPilot size="lg" />
        Pilot mode has ended: this run takes no new readings. Close it and start a new run.
      </div>
    ) : null;

  // Which product, line, run, batch and spec version this is. Both branches show it (UI/UX audit,
  // 31 Oct 2026, UX-39): the start-up branch used to show only "‹ Lines", PILOT and Close run, so an
  // operator walking a changeover could not see which run the answers were landing on.
  const runContext = (r: RunStatus) => (
    <>
      <span className="order-last min-w-0 basis-full truncate text-(length:--fs-md) font-bold sm:order-none sm:basis-auto sm:text-(length:--fs-lg)">
        {r.run.product.name ?? r.run.product.code ?? `Product on run ${r.run.id}`}
      </span>
      {/* On a phone the meta is its own one line under the row (order-last), never three rows. */}
      <span className="order-last basis-full truncate font-mono text-(length:--fs-xs) text-(--muted) sm:order-none sm:basis-auto sm:text-(length:--fs-sm)">
        {r.run.line.name ?? r.run.line.code ?? ''} · run {r.run.id}
        {r.run.d365BatchNumber ? ` · batch ${r.run.d365BatchNumber}` : ''}
        {/* The VERSION NUMBER, never the row id. "spec v41" where 41 is a
            primary key is a different claim from "spec v3", and this line is
            what the record will be read back against. */}
        {r.specVersion ? ` · spec v${r.specVersion.version}` : ''}
      </span>
    </>
  );

  if (run && (run.readiness?.outstanding ?? 0) > 0 && runId) {
    // Closable from here too. A run started by mistake — wrong product, wrong
    // line — must not need its whole start-up walked to be got rid of.
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {pilotStrip}
        <PhotoRefusedBanner />
        <div className="flex flex-none flex-wrap items-center gap-(--s3) border-b border-(--border) bg-(--card) px-(--s4) py-(--s1) sm:px-(--s5) sm:py-(--s2)">
          {linesLink}
          {/* The run's own mark, in its header row now the strip is gone (rule 27). */}
          {run.run.isPilot ? <PilotBadge isPilot /> : null}
          {runContext(run)}
          <span className="ml-auto">{closeControl}</span>
        </div>
        <Walkaround runId={Number(runId)} onChanged={() => setReadinessTick((t) => t + 1)} />
      </div>
    );
  }

  if (error && !run) {
    return (
      <div className="p-(--s5)">
        <p role="alert" className="m-0 text-(length:--fs-lg) font-bold text-(--warn)">
          {error}
        </p>
        <button
          type="button"
          onClick={load}
          className={`mt-(--s4) min-h-[56px] rounded-(--radius) border border-(--border-strong) bg-(--card) px-(--s4) text-(length:--fs-md) font-semibold ${opFocus}`}
        >
          Try again
        </button>
      </div>
    );
  }

  if (!run) {
    return <p role="status" className="p-(--s5) text-(length:--fs-md) text-(--muted)">Loading the run…</p>;
  }

  const dueRemaining = dueNowChecks.filter((c) => !sent.includes(c.obligation.obligationId)).length;
  const stillSending = owed.some((c) => sent.includes(c.obligation.obligationId));
  const nextLater = [...laterChecks].sort(
    (a, b) => Date.parse(a.obligation.dueAt) - Date.parse(b.obligation.dueAt),
  )[0];
  // What the empty panel says. Three different states, and the wrong sentence in
  // any of them sends the operator the wrong way: keyed-but-not-sent is not
  // done, and nothing-due-yet is not nothing-owed.
  const idleMessage = stillSending
    ? 'Every check due now has been keyed. They send as the device gets a connection.'
    : nextLater
      ? `Nothing is due now. Next: ${nextLater.param.name}, ${dueLabel(nextLater.obligation, nowMs)}. ` +
        'To take a check early, tap it under Later.'
      : 'Nothing is owed on this run.';

  const renderCard = (c: OwedCheck) => {
    const isSent = sent.includes(c.obligation.obligationId);
    const isFocused = focused?.obligation.obligationId === c.obligation.obligationId;
    const due = dueLabel(c.obligation, nowMs);
    return (
      <li key={c.obligation.obligationId}>
        <button
          type="button"
          aria-current={isFocused ? 'true' : undefined}
          onClick={() => setFocusedId(c.obligation.obligationId)}
          className={`flex min-h-[56px] w-full flex-col items-start justify-center rounded-(--radius-sm) border px-(--s3) text-left ${opFocus} ${
            isFocused
              ? 'border-(--primary) bg-(--primary-soft)'
              : 'border-(--border) bg-(--card)'
          }`}
        >
          <span className="text-(length:--fs-md) font-semibold">{c.param.name}</span>
          <span className="text-(length:--fs-xs) text-(--muted)">
            {/* "In-process", not the schema's ADHERENCE (UI/UX audit, 31 Oct 2026, UX-18). */}
            {c.obligation.kind === 'FIRST_OFF' ? 'First off' : 'In-process'}
            {/* ⚠ THE DUE TIME, BECAUSE THE NAME IS NOT ENOUGH TO TELL TWO CHECKS
                APART. A `BOTH` parameter puts two cards on this screen with the
                SAME title, and the only thing that distinguishes them is when
                each is actually wanted. Without it an operator standing alone
                reads two identical rows as "it wants two samples now", keys the
                9 o'clock check at 6 and has it accepted. */}
            {due ? ` · ${due}` : ''}
            {c.param.controlPointType !== 'NONE' ? ` · ${c.param.controlPointType}` : ''}
            {/* "Queued", not "done": this device does not decide. The server's
                verdict lands in the run record, and a rejected reading leaves the
                check still owed. */}
            {isSent ? ' · queued' : ''}
          </span>
        </button>
      </li>
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {pilotStrip}
      <PhotoRefusedBanner />
      {/* Run context. An operator must be able to see, without leaving the screen,
          which product and which approved spec version their numbers are landing
          against — that pairing is what the record will say for years. */}
      <div className="flex flex-none flex-wrap items-center gap-(--s3) border-b border-(--border) bg-(--card) px-(--s4) py-(--s2) sm:gap-(--s4) sm:px-(--s5) sm:py-(--s3)">
        {linesLink}
        {run.run.isPilot ? <PilotBadge isPilot /> : null}
        {runContext(run)}
        {run.run.status === 'CLOSED' ? (
          <span className="rounded-(--radius-pill) bg-(--warn-bg) px-(--s3) py-(--s1) text-(length:--fs-sm) font-bold text-(--warn)">
            This run is closed — readings will be refused
          </span>
        ) : null}
        <span className="ml-auto">{closeControl}</span>
      </div>

      {/* Only meaningful once the spec HAS loaded. With no parameters at all every
          owed check counts as an orphan, and that banner would blame the spec's
          contents for a spec that was never read. */}
      {orphanCount > 0 && parameters.length > 0 ? (
        <p
          role="alert"
          className="m-0 flex-none border-b border-(--warn-line) bg-(--warn-bg) px-(--s5) py-(--s2) text-(length:--fs-sm) font-semibold text-(--warn)"
        >
          {orphanCount} owed {orphanCount === 1 ? 'check is' : 'checks are'} not in this run&apos;s
          specification version and cannot be captured here. Tell the office.
        </p>
      ) : null}

      {/* Three columns on a wide screen (the docked iPad in landscape, a desk). Below lg - a phone, or
          an iPad held upright - the same three stack: the owed checks as a strip you scroll sideways,
          the focused check, the pad (27 Oct 2026: the fixed 260 + 360 px columns left a phone 0 px). */}
      {/* 28 Oct 2026, seen on a 375px screen: fixed rows squeezed the focused check to 0px between
          the strip and the pad. Below lg the three are one column that scrolls as a page. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto lg:grid lg:grid-cols-[260px_minmax(0,1fr)_360px] lg:overflow-hidden">
        {/* ── Owed checks, in spec order ── */}
        <nav
          aria-label="Checks owed on this run"
          className="flex flex-none gap-(--s2) overflow-x-auto border-b border-(--border) p-(--s3) lg:block lg:min-h-0 lg:overflow-y-auto lg:border-b-0 lg:border-r"
        >
          {/* One sideways row on a phone: the cards say due now / overdue / due at, so the headings wait for lg. */}
          <h2 className="m-0 hidden px-(--s2) pb-(--s2) text-(length:--fs-xs) font-bold uppercase tracking-[0.06em] text-(--muted-2) lg:block">
            Due now · {dueRemaining} of {dueNowChecks.length}
          </h2>
          <ul className="m-0 flex flex-none list-none gap-(--s2) p-0 lg:flex-col [&>li]:w-[200px] [&>li]:shrink-0 lg:[&>li]:w-auto">
            {dueNowChecks.map(renderCard)}
          </ul>
          {dueNowChecks.length === 0 ? (
            <p className="px-(--s2) text-(length:--fs-md) text-(--muted)">
              {laterChecks.length > 0 ? 'Nothing is due now.' : 'Nothing is owed on this run.'}
            </p>
          ) : null}
          {laterChecks.length > 0 ? (
            <>
              <h2 className="m-0 mt-(--s4) hidden px-(--s2) pb-(--s2) text-(length:--fs-xs) font-bold uppercase tracking-[0.06em] text-(--muted-2) lg:block">
                Later · {laterChecks.length}
              </h2>
              <ul className="m-0 flex flex-none list-none gap-(--s2) p-0 lg:flex-col [&>li]:w-[200px] [&>li]:shrink-0 lg:[&>li]:w-auto">
                {laterChecks.map(renderCard)}
              </ul>
            </>
          ) : null}
        </nav>

        {/* ── The focused check ── */}
        <section className="flex-none p-(--s4) lg:min-h-0 lg:overflow-y-auto lg:p-(--s5)">
          {!param ? (
            // With no check to show there are two different reasons, and saying
            // the wrong one is the worst thing this screen can do: "all keyed" on
            // a run whose spec could not be loaded tells the operator the work is
            // done when nothing has been captured at all.
            <p
              role={error ? 'alert' : undefined}
              className={`m-0 text-(length:--fs-lg) font-semibold ${
                error ? 'text-(--warn)' : 'text-(--muted)'
              }`}
            >
              {error ?? idleMessage}
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-baseline gap-(--s3)">
                <h2 className="m-0 text-(length:--fs-xl) font-bold">{param.name}</h2>
                {param.unit ? (
                  <span className="text-(length:--fs-md) text-(--muted)">
                    in {param.unit}
                  </span>
                ) : null}
                {param.controlPointType !== 'NONE' ? (
                  <span className="rounded-(--radius-pill) bg-(--primary-soft) px-(--s3) py-(--s1) text-(length:--fs-xs) font-bold uppercase tracking-[0.06em]">
                    {param.controlPointType}
                  </span>
                ) : null}
              </div>
              {/* What good looks like, beside the check. Tap to enlarge. */}
              <div className="mt-(--s2)">
                <ReferenceThumbs specParameterId={param.id} photos={param.referencePhotos} source="spec" label={param.name} />
              </div>

              {/* BOTH bands, always, and labelled by what they mean rather than by
                  their column names. "Action" is the band that says adjust the
                  line; "reject" is the one that says the product is out. */}
              <dl className="m-0 mt-(--s3) grid grid-cols-2 gap-(--s3)">
                <div className="rounded-(--radius-sm) bg-(--subtle) p-(--s3)">
                  <dt className="text-(length:--fs-xs) font-bold uppercase tracking-[0.06em] text-(--muted-2)">
                    Action band — adjust the line
                  </dt>
                  <dd className="m-0 mt-(--s1) font-mono text-(length:--fs-md) font-semibold">
                    {bandText(param.actionLower, param.actionUpper, param.unit)}
                  </dd>
                </div>
                <div className="rounded-(--radius-sm) bg-(--subtle) p-(--s3)">
                  <dt className="text-(length:--fs-xs) font-bold uppercase tracking-[0.06em] text-(--muted-2)">
                    Reject band — product is out
                  </dt>
                  <dd className="m-0 mt-(--s1) font-mono text-(length:--fs-md) font-semibold">
                    {bandText(param.minValue, param.maxValue, param.unit)}
                  </dd>
                </div>
              </dl>

              {/* 44px, not --fs-num (28px): this is read at arm's length from a
                  dock, standing beside a running line. The token ramp stops at
                  36px, so the one size in this app that is written as a number
                  rather than a token is the one an operator reads from furthest
                  away. */}
              <p className="m-0 mt-(--s5) font-mono text-[44px] font-bold leading-none">
                {samples[activeSample] || '—'}
                {param.unit ? (
                  <span className="ml-(--s2) text-(length:--fs-lg) font-semibold text-(--muted)">
                    {param.unit}
                  </span>
                ) : null}
              </p>

              <div className="mt-(--s2) flex items-center gap-(--s3)">
                {/* `lg`: the operator reads this from a dock, not from a desk. */}
                {preview ? <VerdictBadge verdict={preview} size="lg" /> : null}
                <span className="text-(length:--fs-sm) text-(--muted)">
                  {preview
                    ? // Words for the aggregation (UI/UX audit, 31 Oct 2026, UX-18): "Preview of AQS".
                      `Preview, judged on ${AGGREGATION_LABEL[param.aggregation].toLowerCase().replace('aqs', 'AQS')} — the server decides when this sends.`
                    : 'Key a value to see the verdict.'}
                </span>
              </div>

              <div className="mt-(--s4)">
                <BandRuler p={param} values={entered} />
              </div>

              {param.sampleCount > 1 ? (
                <div className="mt-(--s5)">
                  <h3 className="m-0 text-(length:--fs-xs) font-bold uppercase tracking-[0.06em] text-(--muted-2)">
                    {param.sampleCount} samples · judged on {AGGREGATION_LABEL[param.aggregation]}
                  </h3>
                  <ul className="m-0 mt-(--s2) flex list-none flex-wrap gap-(--s2) p-0">
                    {samples.map((s, i) => (
                      <li key={i}>
                        <button
                          type="button"
                          aria-label={`Sample ${i + 1}${s ? `, ${s}` : ', empty'}`}
                          aria-current={i === activeSample ? 'true' : undefined}
                          onClick={() => setActiveSample(i)}
                          className={`flex min-h-[56px] min-w-[72px] flex-col items-center justify-center rounded-(--radius-sm) border px-(--s2) font-mono ${opFocus} ${
                            i === activeSample
                              ? 'border-(--primary) bg-(--primary-soft)'
                              : 'border-(--border) bg-(--card)'
                          }`}
                        >
                          <span className="text-(length:--fs-xs) text-(--muted-2)">
                            {i + 1}
                          </span>
                          <span className="text-(length:--fs-md) font-bold">{s || '·'}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {/* An instrument-bound check must name the instrument or the server
                  refuses it with meter_required. Offering the choice here saves a
                  refusal; it does not replace one. */}
              {param.requiresInstrument ? (
                <fieldset className="mt-(--s5) rounded-(--radius-sm) border border-(--border) p-(--s3)">
                  <legend className="px-(--s2) text-(length:--fs-xs) font-bold uppercase tracking-[0.06em] text-(--muted-2)">
                    Instrument {param.instrumentKind ? `· ${param.instrumentKind}` : ''}
                  </legend>
                  <ul className="m-0 flex list-none flex-wrap gap-(--s2) p-0">
                    {candidateMeters.map((m) => {
                      const problem = meterBlock(m);
                      return (
                        <li key={m.id}>
                          <button
                            type="button"
                            aria-pressed={meterId === m.id}
                            onClick={() => setMeterId(m.id)}
                            className={`flex min-h-[56px] flex-col items-start justify-center rounded-(--radius-sm) border px-(--s3) text-left ${opFocus} ${
                              meterId === m.id
                                ? 'border-(--primary) bg-(--primary-soft)'
                                : 'border-(--border) bg-(--card)'
                            }`}
                          >
                            <span className="font-mono text-(length:--fs-md) font-semibold">
                              {m.code}
                            </span>
                            <span
                              className={`text-(length:--fs-xs) ${
                                problem ? 'font-bold text-(--warn)' : 'text-(--muted)'
                              }`}
                            >
                              {problem ?? m.name}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                    {candidateMeters.length === 0 ? (
                      <li className="text-(length:--fs-sm) text-(--muted)">
                        No {param.instrumentKind ?? 'instrument'} is registered. Tell the office —
                        this check cannot be captured without one.
                      </li>
                    ) : null}
                  </ul>
                </fieldset>
              ) : null}

              {param.requiresEvidence ? (
                <div className="mt-(--s4)">
                  <label
                    htmlFor="evidence"
                    className="block text-(length:--fs-xs) font-bold uppercase tracking-[0.06em] text-(--muted-2)"
                  >
                    Evidence reference
                  </label>
                  {/* Slice 1 stores a reference, not a file. The spec says this
                      check needs proof; where that proof lives is the reference. */}
                  <input
                    id="evidence"
                    type="text"
                    value={evidence}
                    onChange={(e) => setEvidence(e.target.value)}
                    placeholder="Photo name, COA number or link"
                    className={`mt-(--s1) min-h-[56px] w-full rounded-(--radius-sm) border border-(--border-strong) bg-(--card) px-(--s3) text-(length:--fs-md) ${opFocus}`}
                  />
                </div>
              ) : null}

              {/* A bad preview only: OUT or ACTION. Optional, before Commit, sent with the reading. */}
              {offerPhoto ? (
                <LinePhotoPicker
                  label={param.name}
                  photos={photos}
                  onAdd={(added) =>
                    setPhotos((prev) => [...prev, ...added.map((p) => ({ ...p, rowKey: null, checkCode: null }))])
                  }
                  onRemove={(key) => setPhotos((prev) => prev.filter((p) => p.key !== key))}
                />
              ) : null}

              {error ? (
                <p
                  role="alert"
                  className="mt-(--s4) rounded-(--radius-sm) bg-(--warn-bg) p-(--s3) text-(length:--fs-md) font-semibold text-(--warn)"
                >
                  {error}
                </p>
              ) : null}
            </>
          )}
        </section>

        {/* ── The pad ── */}
        <aside className="flex-none border-t border-(--border) bg-(--subtle) p-(--s3) lg:min-h-0 lg:overflow-y-auto lg:border-l lg:border-t-0 lg:p-(--s4)">
          <Keypad
            value={samples[activeSample] ?? ''}
            onChange={(next) =>
              setSamples((prev) => prev.map((s, i) => (i === activeSample ? next : s)))
            }
            onCommit={() => void commit()}
            commitLabel={param ? `Commit ${param.name}` : 'Commit'}
            blockedReason={blockedReason}
          />
        </aside>
      </div>
    </div>
  );
}

export default LineCheck;
